import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { CollectiveManagementSocietyCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { CountryCreate } from "../../../country/components/create/CountryCreate";
import { CountryCreateStore } from "../../../country/store";
import { FormControl } from "@mui/material";

export type CollectiveManagementSocietyCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  name: string;
  nameError?: string;
  acronymSelectedValue?: { displayId: string; id: string } | null;
  onAcronymChange?: (value: string) => void;
  acronym: string;
  acronymError?: string;
  countrySelectedValue?: { displayId: string; id: string } | null;
  onCountrySelected?: (value: { displayId: string; id: string } | null) => void;
  onCountryChange?: (value: string) => void;
  countryOptions?: { displayId: string; id: string }[];
  countryError?: string;
  collectiveManagementSocietyCreateStore: CollectiveManagementSocietyCreateStore;
};

export const CollectiveManagementSocietyCreateForm: React.FC<CollectiveManagementSocietyCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      acronym = "",
      acronymError,
      onAcronymChange = () => {},
      onCountrySelected = () => {},
      countrySelectedValue = null,
      countryOptions = [],
      countryError,
      onCountryChange = () => {},
      collectiveManagementSocietyCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="CollectiveManagementSociety"
              mode="create"
              values={{}}
              options={{ country: countryOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "acronym":
                    onAcronymChange?.(valor);
                    break;
                  case "country":
                    onCountrySelected?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={collectiveManagementSocietyCreateStore.getCardErrors(
                "null"
              )}
              title={langStore.groupCollectiveManagementSocietyLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(
                      collectiveManagementSocietyCreateStore.warnings["name"]
                    )
                  }
                  helperText={
                    nameError ||
                    collectiveManagementSocietyCreateStore.warnings["name"]
                      ?.message
                  }
                  label={langStore.CollectiveManagementSocietyNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={
                    langStore.CollectiveManagementSocietyNameDescription
                  }
                />

                <InputString
                  value={acronym}
                  error={Boolean(acronymError)}
                  warning={
                    !acronymError &&
                    Boolean(
                      collectiveManagementSocietyCreateStore.warnings["acronym"]
                    )
                  }
                  helperText={
                    acronymError ||
                    collectiveManagementSocietyCreateStore.warnings["acronym"]
                      ?.message
                  }
                  label={langStore.CollectiveManagementSocietyAcronymLabel}
                  onChange={(value: string) => onAcronymChange(value)}
                  description={
                    langStore.CollectiveManagementSocietyAcronymDescription
                  }
                />
                <InputReference
                  id="country"
                  label={langStore.CollectiveManagementSocietyCountryLabel}
                  error={Boolean(countryError)}
                  helperText={
                    countryError ||
                    collectiveManagementSocietyCreateStore.warnings["country"]
                      ?.message
                  }
                  selectedValue={countrySelectedValue}
                  onChange={(value: string) => onCountryChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onCountrySelected(value)
                  }
                  options={countryOptions}
                  description={
                    langStore.CollectiveManagementSocietyCountryDescription
                  }
                  canCreate={rootStore.authStore.hasPermissions(
                    "country_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new CountryCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <CountryCreate
                          countryCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewCountry"
                      cancelMethod="cancelNewCountry"
                    />
                  )}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
